import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { CostCenterSummary } from '../api/types'
import { CategoryBars } from '../components/charts'
import { Card, EmptyState, ErrorBanner, formatDateTime, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Cost Center: i costi degli account cloud, per giorno.
 *
 * I dati arrivano dai job di discovery cloud (AWS Cost Explorer, Azure Cost
 * Management) e stanno in una tabella a parte, non sui CI: un costo cambia ogni
 * giorno e ha una storia. Ogni job rilegge dal primo del mese scorso, così le
 * correzioni che i provider fanno sui giorni recenti entrano da sole.
 *
 * La pagina risponde a quattro domande, in quest'ordine: quanto sto spendendo
 * questo mese (e rispetto al mese scorso **allo stesso giorno**, l'unico
 * confronto onesto a mese in corso), dove andrò a finire (stima lineare,
 * dichiarata come tale), come sta andando giorno per giorno, e per cosa —
 * servizio, regione, account. Vedi docs/architecture/cost-center.md.
 */

type Periodo = 'finestra' | 'mese' | 'mese_scorso' | '30' | '90'

function isoGiorno(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function intervallo(periodo: Periodo): { from?: string; to?: string } {
  const oggi = new Date()
  const utc = new Date(Date.UTC(oggi.getFullYear(), oggi.getMonth(), oggi.getDate()))
  const primo = new Date(Date.UTC(utc.getUTCFullYear(), utc.getUTCMonth(), 1))
  switch (periodo) {
    case 'mese':
      return { from: isoGiorno(primo), to: isoGiorno(utc) }
    case 'mese_scorso': {
      const fine = new Date(primo.getTime() - 86400000)
      const inizio = new Date(Date.UTC(fine.getUTCFullYear(), fine.getUTCMonth(), 1))
      return { from: isoGiorno(inizio), to: isoGiorno(fine) }
    }
    case '30':
    case '90':
      return { from: isoGiorno(new Date(utc.getTime() - (Number(periodo) - 1) * 86400000)), to: isoGiorno(utc) }
    default:
      return {}
  }
}

function formatoValuta(valuta: string): (n: number) => string {
  try {
    const f = new Intl.NumberFormat(localeCorrente(), {
      style: 'currency',
      currency: valuta || 'USD',
      maximumFractionDigits: 2,
    })
    return (n) => f.format(n)
  } catch {
    // Un codice di valuta che il browser non conosce: il numero con il codice accanto.
    return (n) => `${n.toLocaleString(localeCorrente(), { maximumFractionDigits: 2 })} ${valuta}`
  }
}

function etichettaProvider(p: string): string {
  return { aws: 'AWS', azure: 'Azure', gcp: 'Google Cloud', oci: 'Oracle Cloud' }[p] ?? p
}

/**
 * Come si accende il Cost Center: non c'è un interruttore in CENSO — i costi
 * arrivano con i job di discovery cloud, e ciò che manca sta dal lato del
 * provider (un permesso, un servizio da abilitare). Detto qui, nella pagina
 * che altrimenti resta vuota senza dire perché; aperto quando non c'è niente,
 * chiuso quando i dati ci sono.
 */
function ComeSiAttiva({ aperto }: { aperto: boolean }) {
  return (
    <Card title={t('Come si attiva')}>
      <details open={aperto}>
        <summary>{t('I costi arrivano con i job di discovery cloud della credenziale che ha la spunta «Recupera costi», e serve che il provider li lasci leggere.')}</summary>
        <h4 style={{ marginBottom: 4 }}>{'AWS'}</h4>
        <ol style={{ marginTop: 0 }}>
          <li>{t('Abilita Cost Explorer sull’account: console AWS → Billing and Cost Management → Cost Explorer. La prima attivazione richiede fino a 24 ore, e ogni lettura costa circa 0,01 $.')}</li>
          <li>{t('Aggiungi il permesso ce:GetCostAndUsage alla policy del ruolo o dell’utenza che CENSO usa (la policy completa è nella scheda della credenziale AWS e in docs/operations/aws-permessi.md).')}</li>
          <li>{t('Accendi «Recupera costi» nella credenziale AWS, poi lancia il job di discovery cloud dell’account o aspetta la pianificazione: rilegge dal primo del mese scorso a oggi, così le correzioni del provider entrano da sole.')}</li>
          <li>{t('Apri il rapporto del job: la «Diagnosi AWS» in cima dice se il permesso o Cost Explorer mancano, e cosa fare.')}</li>
        </ol>
        <h4 style={{ marginBottom: 4 }}>{'Azure'}</h4>
        <ol style={{ marginTop: 0 }}>
          <li>{t('Assegna al service principal di CENSO il ruolo «Cost Management Reader» sulla sottoscrizione (Access control (IAM) → Add role assignment).')}</li>
          <li>{t('Accendi «Recupera costi» nella credenziale Azure e lancia il job di discovery cloud della sottoscrizione: i costi entrano con l’inventario. Se il ruolo manca, il rapporto del job lo dice.')}</li>
        </ol>
        <p className="text-muted">
          {t('Google Cloud e Oracle Cloud non sono ancora coperti: su Google i costi reali passano dall’export della fatturazione in BigQuery, su Oracle dalla Usage API.')}
        </p>
        <p>
          <Link to="/credentials">{t('Credenziali')}</Link> · <Link to="/jobs">{t('Job scansioni')}</Link>
        </p>
      </details>
    </Card>
  )
}

type CostoAccount = CostCenterSummary['by_account'][number]

/** The «Per account» table: cost in the period and share of the total. */
function colonneAccount(totale: number, fmt: (n: number) => string): Column<CostoAccount>[] {
  return [
    { key: 'provider', header: t('Fornitore'), value: (a) => etichettaProvider(a.provider) },
    { key: 'account', header: t('Account'), mono: true },
    { key: 'amount', header: t('Costo nel periodo'), type: 'number', render: (a) => fmt(a.amount) },
    {
      key: 'quota',
      header: t('Quota'),
      type: 'number',
      value: (a) => (totale ? Math.round((a.amount / totale) * 100) : null),
      render: (a) => (totale ? `${Math.round((a.amount / totale) * 100)}%` : '—'),
    },
  ]
}

export default function CostCenter() {
  const [periodo, setPeriodo] = useState<Periodo>('finestra')
  // "provider|account": dalla card di un account in Ambienti cloud arriva già
  // scelto (?account=…).
  const [params] = useSearchParams()
  const [account, setAccount] = useState(() => params.get('account') ?? '')
  const [valuta, setValuta] = useState('')
  const [provider, acc] = account ? account.split('|') : ['', '']
  const dati = useAsync(
    () => api.costCenter({ ...intervallo(periodo), provider, account: acc, currency: valuta }),
    [periodo, account, valuta],
  )

  const d = dati.data
  const fmt = useMemo(() => formatoValuta(d?.currency ?? ''), [d?.currency])

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Costi Cloud')}</h1>
          <p className="page__subtitle">
            {t('Quanto costano gli account cloud, giorno per giorno: per servizio, regione e account')}
          </p>
        </div>
        <Link className="chip" to="/cloud-map">{t('Ambienti cloud')}</Link>
      </header>

      <div className="cc-filtri">
        <label className="field">
          <span>{t('Periodo')}</span>
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value as Periodo)}>
            <option value="finestra">{t('Mese scorso e mese in corso')}</option>
            <option value="mese">{t('Mese in corso')}</option>
            <option value="mese_scorso">{t('Mese scorso')}</option>
            <option value="30">{t('Ultimi 30 giorni')}</option>
            <option value="90">{t('Ultimi 90 giorni')}</option>
          </select>
        </label>
        <label className="field">
          <span>{t('Account')}</span>
          <select value={account} onChange={(e) => setAccount(e.target.value)}>
            <option value="">{t('Tutti gli account')}</option>
            {(d?.accounts ?? []).map((a) => (
              <option key={a.provider + a.account} value={`${a.provider}|${a.account}`}>
                {etichettaProvider(a.provider)} · {a.account}
              </option>
            ))}
          </select>
        </label>
        {(d?.currencies.length ?? 0) > 1 && (
          <label className="field">
            <span>{t('Valuta')}</span>
            <select value={valuta || d?.currency} onChange={(e) => setValuta(e.target.value)}>
              {d!.currencies.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
        )}
        {d?.updated_at && (
          <span className="cc-aggiornato">
            {t('Aggiornato: {quando}', { quando: formatDateTime(d.updated_at) })}
          </span>
        )}
      </div>

      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d || d.accounts.length === 0 ? (
        <>
          <Card>
            <EmptyState
              title={t('Ancora nessun costo')}
              hint={t('I costi arrivano con i job di discovery cloud. Su AWS servono il permesso ce:GetCostAndUsage e Cost Explorer abilitato sull’account; su Azure il ruolo Cost Management Reader sulla sottoscrizione. La diagnosi nel rapporto del job dice cosa manca.')}
            />
          </Card>
          <ComeSiAttiva aperto />
        </>
      ) : (
        <>
          <ComeSiAttiva aperto={false} />
          {(d.currencies.length ?? 0) > 1 && (
            <p className="banner banner--info">
              {t('Gli account usano più valute: gli importi sono in {valuta}; le altre si scelgono dal filtro «Valuta».', { valuta: d.currency })}
            </p>
          )}
          <Indicatori d={d} fmt={fmt} />

          <Card title={t('Andamento giornaliero')}>
            <Giornaliero punti={d.daily} fmt={fmt} />
          </Card>

          <div className="cc-griglia">
            <Card title={t('Per servizio')}>
              <CategoryBars
                data={d.by_service.map((s) => ({ key: s.key, label: s.key || '—', value: s.amount }))}
                format={fmt}
              />
            </Card>
            <Card title={t('Per regione')}>
              <CategoryBars
                data={d.by_region.map((s) => ({ key: s.key || '__nessuna__', label: s.key || t('senza regione'), value: s.amount }))}
                format={fmt}
              />
            </Card>
          </div>

          <Card title={t('Per account')}>
            <DataTable
              rows={d.by_account}
              columns={colonneAccount(d.total, fmt)}
              rowKey={(a) => a.provider + a.account}
              conteggio={{ nome: t('account') }}
            />
            <p className="field__hint">
              {t('Costi effettivi come li riporta il fornitore; i giorni più recenti possono ancora cambiare, e il job successivo li corregge.')}
            </p>
          </Card>
        </>
      )}
    </>
  )
}

function Indicatori({ d, fmt }: { d: CostCenterSummary; fmt: (n: number) => string }) {
  const delta =
    d.previous_month_same_day > 0
      ? ((d.month_to_date - d.previous_month_same_day) / d.previous_month_same_day) * 100
      : null
  return (
    <div className="cc-indicatori">
      <div className="cc-indicatore cc-indicatore--principale">
        <span className="cc-indicatore__etichetta">{t('Mese in corso')}</span>
        <span className="cc-indicatore__valore">{fmt(d.month_to_date)}</span>
        {delta !== null && (
          <span className={`cc-delta ${delta > 0 ? 'cc-delta--su' : 'cc-delta--giu'}`}>
            {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%{' '}
            <small>{t('rispetto al mese scorso allo stesso giorno ({importo})', { importo: fmt(d.previous_month_same_day) })}</small>
          </span>
        )}
      </div>
      <div className="cc-indicatore">
        <span className="cc-indicatore__etichetta">{t('Stima di fine mese')}</span>
        <span className="cc-indicatore__valore">{fmt(d.month_end_estimate)}</span>
        <small>{t('media dei giorni trascorsi × giorni del mese: non una previsione')}</small>
      </div>
      <div className="cc-indicatore">
        <span className="cc-indicatore__etichetta">{t('Mese scorso')}</span>
        <span className="cc-indicatore__valore">{fmt(d.previous_month)}</span>
      </div>
      <div className="cc-indicatore">
        <span className="cc-indicatore__etichetta">{t('Totale del periodo')}</span>
        <span className="cc-indicatore__valore">{fmt(d.total)}</span>
        <small>
          {d.from} → {d.to}
        </small>
      </div>
    </div>
  )
}

/** Barre per giorno, con il valore in valuta al passaggio del mouse. */
function Giornaliero({ punti, fmt }: { punti: { day: string; amount: number }[]; fmt: (n: number) => string }) {
  if (!punti.length || punti.every((p) => p.amount === 0)) {
    return <p className="text-muted">{t('Nessun costo nel periodo.')}</p>
  }
  const W = 100
  const H = 40
  const max = Math.max(...punti.map((p) => p.amount), 0.01)
  const passo = W / punti.length
  const larg = Math.max(0.4, passo * 0.7)
  // Una linea sottile al cambio di mese: con due mesi in vista, dove finisce
  // uno e comincia l'altro è la prima cosa che si cerca.
  return (
    <div className="cc-grafico">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={160} role="img"
           aria-label={t('Andamento giornaliero')}>
        <line x1="0" y1={H - 0.3} x2={W} y2={H - 0.3} stroke="var(--divider)" strokeWidth="0.3" />
        {punti.map((p, i) =>
          p.day.endsWith('-01') && i > 0 ? (
            <line key={`m${p.day}`} x1={i * passo} y1="0" x2={i * passo} y2={H} stroke="var(--divider)"
                  strokeWidth="0.25" strokeDasharray="1 1" />
          ) : null,
        )}
        {punti.map((p, i) => {
          const h = (p.amount / max) * (H - 2)
          return (
            <rect key={p.day} x={i * passo + (passo - larg) / 2} y={H - h} width={larg} height={Math.max(h, 0)}
                  rx="0.4" fill="var(--accent)" opacity={0.85}>
              <title>{`${p.day}: ${fmt(p.amount)}`}</title>
            </rect>
          )
        })}
      </svg>
      <div className="cc-grafico__assi">
        <span>{punti[0].day}</span>
        <span>{t('massimo {importo}', { importo: fmt(max) })}</span>
        <span>{punti[punti.length - 1].day}</span>
      </div>
    </div>
  )
}
